import React from 'react';

import { CALIBRATE, CALIBRATE_ANIM } from '../../../../theme/tokens';
import { BokehLayer, Particle, EdgeDisc } from './Bokeh.styles';

/**
 * A dense, transparent-white bokeh field for the calibrating panel: a wide spread of out-of-
 * focus light blobs, from tiny crisp dots to large soft discs (the size variety IS the depth).
 * The whole field racks focus on ONE shared timeline (see Bokeh.styles), with each particle's
 * "depth" offsetting when it peaks, so the focal plane sweeps through it. The icon's ring runs
 * the matching keyframe at depth 0, so it racks in lockstep with the field. Split into a `bg`
 * layer (behind the content) and a sparser `fg` layer (large soft discs drifting in front),
 * for real depth. Purely decorative.
 */
type Spec = {
  id: string;
  layer: 'bg' | 'fg';
  top: string;
  left: string;
  size: number;
  opacity: number;
  blurMin: number;
  blurMax: number;
  /** Focus phase offset (ms into the shared cycle) = this particle's depth. */
  focusDelay: number;
  bx: string;
  by: string;
  driftDur: number;
  driftDelay: number;
};

/** Small deterministic PRNG (mulberry32). A fixed seed makes the scatter stable across
 *  renders/SSR without hand-authoring every particle. */
const mulberry32 = (seed: number): (() => number) => {
  let s = seed >>> 0;
  return (): number => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const COUNT = 16;
/** Half the shared focus cycle: the big edge discs run anti-phase to the centre, so they are
 *  softest exactly when the middle of the field snaps sharp (classic depth-of-field). */
const HALF_CYCLE = Math.round(CALIBRATE_ANIM.focusHuntMs / 2);

// The dense scattered field (deterministic, built once at module load).
const generateField = (): Spec[] => {
  const rand = mulberry32(0x9e3779b1);
  const out: Spec[] = [];
  for (let i = 0; i < COUNT; i += 1) {
    // Size skewed small (mostly fine dots, a few big soft discs), the reference look.
    const size = Math.round(12 + Math.pow(rand(), 12.2) * 180);
    const norm = (size - 3) / 90; // 0 (tiny) .. 1 (big)
    // Depth biased toward 0 so the bulk of the field peaks WITH the ring (delay 0).
    const depth = Math.pow(rand(), 2);
    const layer: 'bg' | 'fg' = size > 95 || rand() < 0.14 ? 'fg' : 'bg';
    out.push({
      id: `p${i}`,
      layer,
      top: `${(rand() * 100).toFixed(2)}%`,
      left: `${(rand() * 100).toFixed(2)}%`,
      size,
      // Big discs softer + fainter, small dots brighter + crisper.
      opacity: Number((0.5 - norm * 0.28 + rand() * 0.14).toFixed(3)),
      blurMin: Number((0.1 + norm * 0.4).toFixed(2)),
      blurMax: Number((2 + norm * 24).toFixed(2)),
      focusDelay: Math.round(depth * CALIBRATE_ANIM.focusHuntMs),
      bx: `${Math.round((rand() - 0.5) * 128)}px`,
      by: `${Math.round((rand() - 0.5) * 128)}px`,
      driftDur: Math.round(CALIBRATE_ANIM.driftMs * (0.9 + rand() * 0.6)),
      driftDelay: Math.round(CALIBRATE_ANIM.driftMs * rand() * 0.5),
    });
  }
  return out;
};

const SPECS: Spec[] = generateField();

/** Big, very soft foreground discs anchored to the left / right SIDES (mostly off-frame), for
 *  a strong depth frame like the Apple bokeh intro. Sized as a % of the panel WIDTH (so they
 *  scale and hug the edge on any panel size), never crisp (high blur), and anti-phase to the
 *  centre (HALF_CYCLE), so they bloom softest just as the middle of the field snaps sharp. */
type EdgeSpec = {
  id: string;
  side: 'left' | 'right';
  /** How far the disc is pushed off its edge (% of panel width). */
  offset: string;
  top: string;
  /** Diameter as a % of panel width. */
  size: string;
  opacity: number;
  blurMin: number;
  blurMax: number;
  bx: string;
  by: string;
  driftDur: number;
  driftDelay: number;
};

const D = (factor: number): number => Math.round(CALIBRATE_ANIM.driftMs * factor);

const EDGE_DISCS: EdgeSpec[] = [
  { id: 'el1', side: 'left', offset: '-15%', top: '6%', size: '38%', opacity: 0.82, blurMin: 30, blurMax: 46, bx: '20px', by: '16px', driftDur: D(5.18), driftDelay: D(0.2) },
  { id: 'el2', side: 'left', offset: '-40%', top: '58%', size: '90%', opacity: 0.54, blurMin: 30, blurMax: 46, bx: '16px', by: '-14px', driftDur: D(10.34), driftDelay: D(0.4) },
  { id: 'er1', side: 'right', offset: '-40%', top: '-20%', size: '72%', opacity: 0.8, blurMin: 30, blurMax: 46, bx: '-20px', by: '14px', driftDur: D(5.26), driftDelay: D(0.2) },
  { id: 'er2', side: 'right', offset: '-19%', top: '74%', size: '40%', opacity: 0.72, blurMin: 30, blurMax: 46, bx: '-16px', by: '-16px', driftDur: D(10.12), driftDelay: D(0.4) },
];

export type BokehFieldProps = {
  layer: 'bg' | 'fg';
  /** Stacking position within the panel (bg behind the content, fg in front). */
  z: number;
};

export const BokehField = ({ layer, z }: BokehFieldProps): React.ReactElement => (
  <BokehLayer $z={z} aria-hidden="true">
    {SPECS.filter((s) => s.layer === layer).map((s) => (
      <Particle
        key={s.id}
        $top={s.top}
        $left={s.left}
        $size={s.size}
        $color={CALIBRATE.bokehWhite}
        $opacity={s.opacity}
        $blurMin={s.blurMin}
        $blurMax={s.blurMax}
        $focusDelay={s.focusDelay}
        $bx={s.bx}
        $by={s.by}
        $driftDur={s.driftDur}
        $driftDelay={s.driftDelay}
      />
    ))}
    {/* Big side discs live in the foreground layer, framing the panel edges. */}
    {layer === 'fg' &&
      EDGE_DISCS.map((e) => (
        <EdgeDisc
          key={e.id}
          $side={e.side}
          $offset={e.offset}
          $top={e.top}
          $size={e.size}
          $color={CALIBRATE.bokehWhite}
          $opacity={e.opacity}
          $blurMin={e.blurMin}
          $blurMax={e.blurMax}
          $focusDelay={HALF_CYCLE}
          $bx={e.bx}
          $by={e.by}
          $driftDur={e.driftDur}
          $driftDelay={e.driftDelay}
        />
      ))}
  </BokehLayer>
);

export default BokehField;
